import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../../../services/api';
import { AnexoOrcamento } from '../../../../types/anexos_orcamento';
import { ProdutoOportunidade } from '../../../../types/orcamento';
import { Fornecedor } from '../../../../types/fornecedor';
import { Produto } from '../../../../types/produtos';
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { Modal, Dropdown } from 'react-bootstrap';
import AnexoOrcamentoForm from './AnexoOrcamentoForm';

interface AnexosOrcamentoListProps {
    opportunityId: number;
    // Muda quando a aba de itens altera produtos, para atualizar a lista usada no formulário
    refreshKey?: number;
}

const AnexosOrcamentoList: React.FC<AnexosOrcamentoListProps> = ({ opportunityId, refreshKey = 0 }) => {
    const [anexos, setAnexos] = useState<AnexoOrcamento[]>([]);
    const [loading, setLoading] = useState(true);
    const [produtos, setProdutos] = useState<ProdutoOportunidade[]>([]);
    const [produtosDetalhes, setProdutosDetalhes] = useState<Record<number, Produto>>({});
    const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
    
    // Estados para modais
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [selectedAnexo, setSelectedAnexo] = useState<AnexoOrcamento | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Buscar todos os anexos da oportunidade
    const fetchAnexos = async () => {
        try {
            setLoading(true);
            const response = await apiService.getAnexosOrcamento({
                id_oportunidade: opportunityId
            });

            if (response.success && Array.isArray(response.records)) {
                setAnexos(response.records);
            } else {
                setAnexos([]);
            }
        } catch (error) {
            console.error('Erro ao carregar anexos de orçamento:', error);
        } finally {
            setLoading(false);
        }
    };

    // Buscar produtos da oportunidade (para o formulário de anexo)
    const fetchProdutos = async () => {
        try {
            const response = await apiService.getProdutosOportunidade(opportunityId);
            if (response.success && Array.isArray(response.data)) {
                setProdutos(response.data);
                
                // Buscar detalhes dos produtos em lote (1 chamada)
                const produtoIds = response.data.map(p => p.id_produto);
                const produtosMap: Record<number, Produto> = {};

                try {
                    const batchRes = await apiService.getProdutosByIds(produtoIds);
                    if (batchRes.success && Array.isArray(batchRes.data)) {
                        batchRes.data.forEach(p => { produtosMap[p.id] = p; });
                    }
                } catch (err) {
                    console.error('Erro ao buscar detalhes dos produtos:', err);
                }

                setProdutosDetalhes(produtosMap);
            }
        } catch (error) {
            console.error('Erro ao carregar produtos:', error);
        }
    };

    // Buscar fornecedores para o formulário
    const fetchFornecedores = async () => {
        try {
            const response = await apiService.getFornecedores();
            if (response.success && Array.isArray(response.data || response.records)) {
                setFornecedores(response.data || response.records);
            }
        } catch (error) {
            console.error('Erro ao carregar fornecedores:', error);
        }
    };

    useEffect(() => {
        fetchAnexos();
        fetchProdutos();
        fetchFornecedores();
    }, [opportunityId]);

    // Quando os itens mudam em outra aba, atualiza só os produtos do formulário
    useEffect(() => {
        if (refreshKey > 0) {
            fetchProdutos();
        }
    }, [refreshKey]);

    // Formatação de tamanho do arquivo
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Obter o nome do produto baseado no id
    const getProdutoNome = (idProdutoOportunidade?: number): string => {
        if (!idProdutoOportunidade) return 'Não especificado';
        
        const produtoOportunidade = produtos.find(p => p.id === idProdutoOportunidade);
        if (!produtoOportunidade) return 'Não especificado';
        
        const produto = produtosDetalhes[produtoOportunidade.id_produto];
        if (produto) {
            return produto.nome;
        }
        
        return 'Produto ID: ' + produtoOportunidade.id_produto;
    };

    // Formatar data e hora 
    const formatDateTime = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Determinar ícone baseado no tipo do arquivo
    const getFileIcon = (tipo: string): string => {
        if (tipo.includes('image')) return 'photo';
        if (tipo.includes('pdf')) return 'file-text';
        if (tipo.includes('spreadsheet') || tipo.includes('excel')) return 'table';
        if (tipo.includes('word') || tipo.includes('document')) return 'file-text';
        if (tipo.includes('zip') || tipo.includes('rar')) return 'archive';
        return 'file';
    };

    // Manipuladores para ações
    const handleAddAnexo = () => {
        setShowAddModal(true);
    };

    const handleEditAnexo = (anexo: AnexoOrcamento) => {
        setSelectedAnexo(anexo);
        setShowEditModal(true);
    };

    const handlePrepareDelete = (anexo: AnexoOrcamento) => {
        setSelectedAnexo(anexo);
        setShowDeleteModal(true);
    };

    const handleDeleteAnexo = async () => {
        if (!selectedAnexo) return;
        
        try {
            setIsDeleting(true);
            const response = await apiService.deleteAnexoOrcamento(selectedAnexo.id);
            
            if (response.success) {
                setShowDeleteModal(false);
                setSuccessMessage('Anexo excluído com sucesso!');
                setShowSuccessModal(true);
                
                // Atualizar a lista após excluir
                setTimeout(() => {
                    fetchAnexos();
                    setShowSuccessModal(false);
                }, 2000);
            }
        } catch (error) {
            console.error('Erro ao excluir anexo:', error);
            alert('Erro ao excluir anexo. Por favor, tente novamente.');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDownload = async (anexo: AnexoOrcamento) => {
        try {
            await apiService.downloadAnexoOrcamento(anexo.id);
        } catch (error) {
            console.error('Erro ao fazer download do anexo:', error);
            alert('Erro ao fazer download do anexo. Por favor, tente novamente.');
        }
    };

    const handleAnexoSaved = () => {
        setShowAddModal(false);
        setShowEditModal(false);
        setSuccessMessage(selectedAnexo 
            ? 'Anexo atualizado com sucesso!' 
            : 'Anexo criado com sucesso!');
        setShowSuccessModal(true);
        
        // Atualizar a lista e fechar o modal de sucesso após 2 segundos
        setTimeout(() => {
            fetchAnexos();
            setShowSuccessModal(false);
        }, 2000);
    };

    return (
        <div className="anexos-orcamento-list">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="card-title mb-0">Anexos de Orçamento</h4>
                <button 
                    type="button" 
                    className="btn btn-primary" 
                    onClick={handleAddAnexo}
                >
                    <i className="ti ti-plus me-1"></i> Novo Anexo
                </button>
            </div>

            {loading ? (
                <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                </div>
            ) : anexos.length === 0 ? (
                <div className="card">
                    <div className="card-body text-center py-5">
                        <div className="avatar avatar-xl bg-light-primary rounded-circle mb-4">
                            <i className="ti ti-file-upload fs-24 text-primary"></i>
                        </div>
                        <h5>Nenhum anexo de orçamento encontrado</h5>
                        <p className="text-muted">
                            Clique no botão "Novo Anexo" para adicionar um anexo de orçamento.
                        </p>
                    </div>
                </div>
            ) : (
                <div className="row">
                    {anexos.map((anexo) => (
                        <div className="col-md-6 col-xl-4 mb-3" key={anexo.id}>
                            <div className="card h-100">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-3">
                                        <div className="avatar bg-light-primary rounded">
                                            <i className={`ti ti-${getFileIcon(anexo.tipo_arquivo)} text-primary fs-20`}></i>
                                        </div>
                                        <Dropdown>
                                            <Dropdown.Toggle 
                                                as="a" 
                                                className="dropdown-toggle d-flex align-items-center" 
                                                id={`dropdown-anexo-${anexo.id}`} 
                                                variant="link"
                                            >
                                                <i className="ti ti-dots-vertical"></i>
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu align="end">
                                                <Dropdown.Item onClick={() => handleDownload(anexo)}>
                                                    <i className="ti ti-download me-2"></i>Download
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => handleEditAnexo(anexo)}>
                                                    <i className="ti ti-edit me-2"></i>Editar
                                                </Dropdown.Item>
                                                <Dropdown.Item 
                                                    onClick={() => handlePrepareDelete(anexo)}
                                                    className="text-danger"
                                                >
                                                    <i className="ti ti-trash me-2"></i>Excluir
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>
                                    
                                    <h5 className="card-title mb-2">{anexo.titulo}</h5>
                                    
                                    {anexo.descricao && (
                                        <p className="text-muted mb-3">{anexo.descricao}</p>
                                    )}
                                    
                                    <div className="file-info">
                                        <span className="badge bg-light-info text-info mb-2">
                                            {anexo.tipo_documento}
                                        </span>
                                        
                                        <div className="mb-1">
                                            <i className="ti ti-package me-1"></i>
                                            <span className="text-muted">Produto: </span>
                                            {getProdutoNome(anexo.id_produto_oportunidade)}
                                        </div>
                                        
                                        <div className="mb-1">
                                            <i className="ti ti-building-store me-1"></i>
                                            <span className="text-muted">Fornecedor: </span>
                                            {anexo.id_fornecedor && fornecedores.find(f => f.id === anexo.id_fornecedor)?.nome || 'Não especificado'}
                                        </div>
                                        
                                        <div className="mb-1">
                                            <i className="ti ti-file me-1"></i>
                                            <span className="text-muted">Arquivo: </span>
                                            {anexo.nome_arquivo} ({formatFileSize(anexo.tamanho_arquivo)})
                                        </div>
                                        
                                        <div className="d-flex align-items-center mt-3">
                                            <div className="avatar avatar-xs me-2">
                                                <ImageWithBasePath
                                                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(anexo.usuario_nome)}&background=0D8ABC&color=fff`}
                                                    alt={anexo.usuario_nome}
                                                    className="rounded-circle"
                                                />
                                            </div>
                                            <div>
                                                <span className="text-muted small">Enviado por </span>
                                                <span className="small fw-medium">{anexo.usuario_nome}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-footer bg-transparent border-top-0 pt-0">
                                    <div className="d-flex justify-content-between">
                                        <span className="text-muted small">
                                            {formatDateTime(anexo.data_upload)}
                                        </span>
                                        <button 
                                            onClick={() => handleDownload(anexo)} 
                                            className="btn btn-sm btn-outline-primary"
                                        >
                                            <i className="ti ti-download me-1"></i>
                                            Download
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal para adicionar anexo */}
            <Modal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                centered
                backdrop="static"
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Adicionar Anexo de Orçamento</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <AnexoOrcamentoForm
                        opportunityId={opportunityId}
                        produtos={produtos}
                        fornecedores={fornecedores}
                        onSave={handleAnexoSaved}
                        onCancel={() => setShowAddModal(false)}
                    />
                </Modal.Body>
            </Modal>

            {/* Modal para editar anexo */}
            <Modal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                centered
                backdrop="static"
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Editar Anexo de Orçamento</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <AnexoOrcamentoForm
                        opportunityId={opportunityId}
                        produtos={produtos}
                        fornecedores={fornecedores}
                        anexo={selectedAnexo}
                        onSave={handleAnexoSaved}
                        onCancel={() => setShowEditModal(false)}
                    />
                </Modal.Body>
            </Modal>

            {/* Modal de confirmação para exclusão */}
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
            >
                <Modal.Body>
                    <div className="text-center">
                        <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                            <i className="ti ti-trash-x fs-36 text-danger" />
                        </div>
                        <h4 className="mb-2">Remover Anexo?</h4>
                        <p className="mb-0">
                            Tem certeza que deseja remover este anexo de orçamento?
                            Esta ação não poderá ser desfeita.
                        </p>
                        <div className="d-flex align-items-center justify-content-center mt-4">
                            <button
                                type="button"
                                className="btn btn-light me-2"
                                onClick={() => setShowDeleteModal(false)}
                                disabled={isDeleting}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleDeleteAnexo}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Excluindo...
                                    </>
                                ) : (
                                    'Sim, Excluir'
                                )}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Modal de sucesso */}
            <Modal
                show={showSuccessModal}
                onHide={() => setShowSuccessModal(false)}
                centered
            >
                <Modal.Body>
                    <div className="success-message text-center">
                        <div className="success-popup-icon bg-success-light">
                            <i className="ti ti-check-check fs-36 text-success" />
                        </div>
                        <h3>{successMessage}</h3>
                        <p>A operação foi realizada com sucesso.</p>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default AnexosOrcamentoList;